import { API_TYPES } from "@src/appV2/api";
import { z } from "zod";

export const WORKER_CREDENTIAL_TYPE = "worker-credential";
export const WORKER_CREDENTIAL_REQUEST_TYPE = "worker-credential-request";

// eslint-disable-next-line no-restricted-syntax
export enum ChartingCredentialSoftware {
  POINT_CLICK_CARE = "PointClickCare",
  MATRIX = "Matrix",
  NETSMART = "NetSmart",
  AMERICAN_HEALTH_TECH = "American Health Tech",
  OTHER = "Other",
}

const chartingCredentialSource = z.enum([
  "workplace-chat",
  "workplace-bulk-sharing",
  "workplace-professionals",
  "workplace",
  "worker",
  "chat-parser",
  "chat-detected-and-saved",
  "auto-provision",
]);

export const createWorkerCredentialRequestRequestSchema = z.object({
  data: z.object({
    type: z.literal(WORKER_CREDENTIAL_REQUEST_TYPE),
    id: z.string(),
    relationships: z.object({
      worker: z.object({
        data: z.object({
          type: z.literal(API_TYPES.worker),
          id: z.string(),
        }),
      }),
      workplace: z.object({
        data: z.object({
          type: z.literal(API_TYPES.workplace),
          id: z.string(),
        }),
      }),
    }),
  }),
});

export const createWorkerCredentialRequestResponseSchema = z.string();

export type CreateWorkerCredentialRequestRequest = z.infer<
  typeof createWorkerCredentialRequestRequestSchema
>;
export type CreateWorkerCredentialRequestResponse = z.infer<
  typeof createWorkerCredentialRequestResponseSchema
>;

export const workerCredentialsAttributesSchema = z.object({
  workerId: z.string(),
  workplaceId: z.string(),
  chartingCredentials: z
    .object({
      software: z.nativeEnum(ChartingCredentialSoftware).optional(),
      username: z.string().optional(),
      password: z.string().optional(),
      source: chartingCredentialSource.optional(),
      expiresAt: z.coerce.date().optional(),
    })
    .optional(),
  timeclockCredentials: z
    .object({ pin: z.string(), badgeNumber: z.string().optional() })
    .optional(),
});

export const createWorkerCredentialsRequestSchema = z.object({
  data: z.object({
    type: z.literal(WORKER_CREDENTIAL_TYPE),
    attributes: workerCredentialsAttributesSchema,
  }),
});

export const createWorkerCredentialsResponseSchema = z.object({
  data: z.object({
    type: z.literal(WORKER_CREDENTIAL_TYPE),
    id: z.string(),
    attributes: workerCredentialsAttributesSchema,
  }),
});

export const getCredentialsForShiftsQuerySchema = z.object({
  filter: z.object({
    startDate: z.string().datetime(),
    endDate: z.string().datetime().optional(),
  }),
});

export const workerCredentialsResponseSchema = z.object({
  data: z.array(
    z.object({
      type: z.literal(API_TYPES.workerCredential),
      id: z.string(),
      attributes: workerCredentialsAttributesSchema,
    })
  ),
});

const GetWorkerCredentialsForShiftsShiftRelationshipSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.shift),
    id: z.string(),
  }),
});

const shiftIncludedSchema = z.object({
  type: z.literal(API_TYPES.shift),
  id: z.string(),
  attributes: z.object({
    startDate: z.string().datetime(),
    endDate: z.string().datetime(),
    timeSlot: z.enum(["am", "pm", "noc"]),
    qualification: z.string(),
  }),
});

const GetWorkerCredentialsForShiftsWorkerRelationshipSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.worker),
    id: z.string(),
  }),
});

const workerIncludedSchema = z.object({
  type: z.literal(API_TYPES.worker),
  id: z.string(),
  attributes: z.object({ name: z.string() }),
});

const GetWorkerCredentialsForShiftsRelationshipsSchema = z.object({
  shift: GetWorkerCredentialsForShiftsShiftRelationshipSchema,
  worker: GetWorkerCredentialsForShiftsWorkerRelationshipSchema,
});

const GetWorkerCredentialsForShiftsIncludedSchema = z.discriminatedUnion("type", [
  shiftIncludedSchema,
  workerIncludedSchema,
]);

export const getWorkerCredentialsForShiftsResponseSchema = z.object({
  data: z.array(
    z.object({
      type: z.literal(API_TYPES.workerCredential),
      id: z.string(),
      attributes: workerCredentialsAttributesSchema,
      relationships: GetWorkerCredentialsForShiftsRelationshipsSchema,
    })
  ),
  included: z.array(GetWorkerCredentialsForShiftsIncludedSchema),
});

export const credentialsFormSchema = z
  .object({
    chartingSoftware: z.nativeEnum(ChartingCredentialSoftware),
    username: z.string(),
    password: z.string(),
    timeclockPin: z.string().optional(),
    timeclockBadge: z.string().optional(),
    externalId: z.string().optional(),
  })
  .superRefine((data, context) => {
    // FIXME: Automatically disabled during ESLint config update
    const hasUsername = Boolean(data.username);
    // FIXME: Automatically disabled during ESLint config update
    const hasPassword = Boolean(data.password);

    if (hasUsername !== hasPassword) {
      if (hasUsername) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Password is required when username is provided",
          path: ["password"],
        });
      } else {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Username is required when password is provided",
          path: ["username"],
        });
      }
    }
  });

export type WorkerCredentialsAttributes = z.infer<typeof workerCredentialsAttributesSchema>;
export type CreateWorkerCredentialsRequest = z.infer<typeof createWorkerCredentialsRequestSchema>;
export type CreateWorkerCredentialsResponse = z.infer<typeof createWorkerCredentialsResponseSchema>;
export type WorkerCredentialsResponse = z.infer<typeof workerCredentialsResponseSchema>;
export type CredentialsFormData = z.infer<typeof credentialsFormSchema>;
export type GetWorkerCredentialsForShiftsQuery = z.infer<typeof getCredentialsForShiftsQuerySchema>;
export type GetWorkerCredentialsForShiftsResponse = z.infer<
  typeof getWorkerCredentialsForShiftsResponseSchema
>;
export type ShiftIncluded = z.infer<typeof shiftIncludedSchema>;
export type WorkerIncluded = z.infer<typeof workerIncludedSchema>;
export type ChartingCredentialSource = z.infer<typeof chartingCredentialSource>;
